import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/shadcn-io/spinner";
import { useTranslation } from "@/features/utils/i18n";
import { useState } from "react";
import { toast } from "sonner";

/**
 * A bulk action is the grouped counterpart of a row action ("..." menu): the
 * same operation, applied to every selected row. Pages declare them
 * declaratively so the toolbar (confirmation, pending state, selection reset)
 * stays identical everywhere.
 */
export interface BulkAction<TData> {
  key: string;
  label: string;
  icon?: React.ReactNode;
  variant?: React.ComponentProps<typeof Button>["variant"];
  /** Confirmation text shown before running — omit for non-destructive actions. */
  confirm?: (rows: TData[]) => string;
  /** Hide the action for the current selection (e.g. nothing to approve). */
  isVisible?: (rows: TData[]) => boolean;
  onAction: (rows: TData[]) => void | Promise<void>;
}

/**
 * Runs `fn` over every selected row with a small concurrency window and reports
 * a single toast instead of one per row. Failures never abort the batch: the
 * rows that can be processed are, and the count that failed is surfaced.
 */
export const runBulk = async <T,>(
  items: T[],
  fn: (item: T) => Promise<unknown>,
  messages: {
    success: (count: number) => string;
    error: (count: number) => string;
  },
  concurrency = 4,
): Promise<{ succeeded: number; failed: number }> => {
  const queue = [...items];
  let succeeded = 0;
  let failed = 0;

  const worker = async () => {
    for (;;) {
      const item = queue.shift();
      if (item === undefined) return;
      try {
        await fn(item);
        succeeded++;
      } catch {
        failed++;
      }
    }
  };

  await Promise.all(
    Array.from({ length: Math.min(concurrency, items.length) }, worker),
  );

  if (succeeded > 0) toast.success(messages.success(succeeded));
  if (failed > 0) toast.error(messages.error(failed));

  return { succeeded, failed };
};

export function BulkActionsBar<TData>({
  selectedRows,
  actions,
  onClear,
}: {
  selectedRows: TData[];
  actions: BulkAction<TData>[];
  onClear: () => void;
}) {
  const { t } = useTranslation("table");
  const [pendingKey, setPendingKey] = useState<string | null>(null);

  if (selectedRows.length === 0) return null;

  const visibleActions = actions.filter(
    (action) => action.isVisible?.(selectedRows) ?? true,
  );

  const handleAction = async (action: BulkAction<TData>) => {
    setPendingKey(action.key);
    try {
      await action.onAction(selectedRows);
      onClear();
    } finally {
      setPendingKey(null);
    }
  };

  return (
    <div className="bg-muted/40 flex flex-wrap items-center gap-2 rounded-sm border px-4 py-2">
      <span className="text-sm font-medium">
        {t("table.selected", { count: selectedRows.length })}
      </span>
      <div className="flex-1" />
      {visibleActions.map((action) => (
        <Button
          key={action.key}
          size="sm"
          variant={action.variant ?? "outline"}
          disabled={pendingKey !== null}
          confirm={action.confirm?.(selectedRows)}
          onClick={() => handleAction(action)}
        >
          {pendingKey === action.key ? (
            <Spinner className="h-4 w-4" />
          ) : (
            action.icon
          )}
          {action.label}
        </Button>
      ))}
      <Button
        size="sm"
        variant="ghost"
        onClick={onClear}
        disabled={pendingKey !== null}
      >
        {t("table.clearSelection")}
      </Button>
    </div>
  );
}
